import { Spinner } from '../../components';
import { SubmittedMeter } from '../exams/ExamCharts';
import { ResultsCardFrame } from '../results/ResultsCardFrame';
import type { ResultSession } from '../results/sessions';
import { useResultSessionStore } from '../results/store';
import { formatAverage, taskGradingStats, type GradingStats } from './gradingStats';
import { shownRoster, statusLabel, vnDateTime, type StudentSubmission } from './submissions';
import { useClassSubmissions } from './useClassSubmissions';

function statusClass(submission: StudentSubmission | null): string {
  if (!submission) return 'hw-status hw-status--missing';
  return submission.approved ? 'hw-status hw-status--marked' : 'hw-status hw-status--submitted';
}

// anonymous by construction: counts, an average and bands, never a name, so it shows while projected
function GradingSummary({ stats }: { stats: GradingStats }) {
  const bands = stats.bands;
  const max = bands ? Math.max.apply(null, bands.map((band) => band.count)) : 0;
  return (
    <section className="quiz-results__block">
      <h3 className="quiz-results__block-title">Chấm điểm</h3>
      <div className="hw-stats">
        <p className="hw-stats__line">
          <strong>{stats.graded}/{stats.submitted}</strong> bài đã chấm
          {stats.average !== null ? <> • Điểm TB <strong>{formatAverage(stats.average)}</strong></> : null}
        </p>
        {bands ? (
          <div className="hw-stats__bars" role="img" aria-label={`Phân bố điểm: ${bands.map((band) => `${band.label}: ${band.count}`).join(', ')}`}>
            {bands.map((band) => (
              <div key={band.label} className="hw-stats__band">
                <span className="hw-stats__count">{band.count}</span>
                <span className="hw-stats__track"><span className="hw-stats__fill" style={{ height: `${max > 0 ? Math.round((band.count / max) * 100) : 0}%`, background: band.color }} /></span>
                <span className="hw-stats__label">{band.label}</span>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </section>
  );
}

export interface HomeworkResultsCardProps {
  session: ResultSession;
  onClose: () => void;
  onBackToDe: () => void;
}

// Kết quả for the homework on this tab; with marks hidden Chi tiết lists only who submitted
export function HomeworkResultsCard({ session, onClose, onBackToDe }: HomeworkResultsCardProps) {
  const { roster, loading, error, tasks, courseTitles, reload } = useClassSubmissions();
  const setPanelTab = useResultSessionStore((state) => state.setPanelTab);
  const setShowScores = useResultSessionStore((state) => state.setShowScores);
  const openStudent = useResultSessionStore((state) => state.openStudent);
  const task = tasks.filter((item) => item.id === session.refId)[0] || null;

  const subtitle = [task ? task.title : session.title, task ? courseTitles[task.courseId] || '' : ''].filter(Boolean).join(' • ');
  const actions = (
    <>
      <button type="button" className="hw-panel__refresh" aria-pressed={session.showScores} onClick={() => setShowScores(session.tabId, !session.showScores)}>{session.showScores ? 'Ẩn điểm' : 'Hiện điểm'}</button>
      {session.student ? <button type="button" className="hw-panel__refresh" onClick={onBackToDe}>Quay lại đề</button> : null}
    </>
  );

  let body;
  if (!task) {
    body = loading || roster.length === 0 ? <Spinner label="Đang tải bài nộp của lớp…" />
      : error ? <p className="quiz-results__empty">Chưa tải được bài nộp: {error} <button type="button" className="hw-panel__refresh" onClick={reload}>Thử lại</button></p>
        : <p className="quiz-results__empty">Chưa có học sinh nào nộp bài này trên IZTeach.</p>;
  } else if (session.panelTab === 'stats') {
    body = <><SubmittedMeter submitted={task.count} total={roster.length} /><GradingSummary stats={taskGradingStats(task)} /></>;
  } else {
    const entries = shownRoster(task, roster, session.showScores);
    body = (
      <section className="quiz-results__block">
        {session.showScores ? null : <p className="quiz-results__empty">Đang hiện học sinh đã nộp. Bấm Hiện điểm để xem trạng thái chấm và học sinh chưa nộp.</p>}
        <ul className="hw-roster">
          {entries.map((entry) => {
            const submission = entry.submission;
            const active = entry.student.code === session.student;
            return (
              <li key={entry.student.code}>
                <button type="button" className={active ? 'hw-roster__row is-selected' : 'hw-roster__row'} aria-current={active ? 'true' : undefined} disabled={!submission} onClick={() => openStudent(session.tabId, entry.student.code)}>
                  <span className="hw-roster__name">{entry.student.name}</span>
                  <span className="hw-roster__meta">
                    {session.showScores ? <span className={statusClass(submission)}>{statusLabel(submission)}</span> : null}
                    {submission && submission.submittedAt ? <span className="hw-roster__time">{vnDateTime(submission.submittedAt)}</span> : null}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </section>
    );
  }

  return (
    <ResultsCardFrame label="Kết quả bài tập" subtitle={subtitle} actions={actions} tab={session.panelTab} onTab={(tab) => setPanelTab(session.tabId, tab)} onClose={onClose}>
      {body}
    </ResultsCardFrame>
  );
}
